import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Spinner } from "@/components/states";
import { useAuth } from "@/lib/auth";
import { customerErrorMessage } from "@/lib/customer-error";
import { useI18n } from "@/lib/i18n";
import { isValidMarketPhone, normalizePhoneForMarket, toLatinDigits } from "@/lib/format";
import { useStorefront } from "@/lib/storefront";

export function LoginForm({ onSuccess }: { onSuccess?: () => void }) {
  const { t } = useI18n();
  const { tenant } = useStorefront();
  const { sendCode, resend, verify, completeRegistration } = useAuth();
  const [step, setStep] = useState<"phone" | "otp" | "name">("phone");
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSend() {
    if (loading) return;
    const normalized = normalizePhoneForMarket(phone, tenant.countryCode);
    if (!isValidMarketPhone(normalized, tenant.countryCode)) {
      setError(t("orderType.invalidPhone"));
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await sendCode(normalized);
      setPhone(normalized);
      setStep("otp");
    } catch (error) {
      toast.error(customerErrorMessage(error, t, "login.smsUnavailable"));
    } finally {
      setLoading(false);
    }
  }

  async function handleVerify(value = code) {
    if (loading || value.length !== 4) return;
    setLoading(true);
    setError(null);
    try {
      const result = await verify(phone, value);
      if (result === "authenticated") onSuccess?.();
      else setStep("name");
    } catch (error) {
      setError(customerErrorMessage(error, t, "login.invalidOtp"));
      setCode("");
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    setResending(true);
    try {
      await resend(phone);
      toast.success(t("login.resend"));
    } catch (error) {
      toast.error(customerErrorMessage(error, t, "login.smsUnavailable"));
    } finally {
      setResending(false);
    }
  }

  async function handleComplete() {
    if (loading || name.trim().length < 2) return;
    setLoading(true);
    setError(null);
    try {
      await completeRegistration(name);
      onSuccess?.();
    } catch (error) {
      setError(customerErrorMessage(error, t, "login.registrationExpired"));
    } finally {
      setLoading(false);
    }
  }

  if (step === "phone") {
    return (
      <form className="grid gap-4" onSubmit={(event) => { event.preventDefault(); void handleSend(); }}>
        <p className="text-sm text-muted-foreground">{t("login.phoneStep")}</p>
        <div className="grid gap-2">
          <Label htmlFor="login-phone">{t("orderType.phone")}</Label>
          <Input
            id="login-phone"
            inputMode="tel"
            type="tel"
            autoComplete="tel"
            required
            dir="ltr"
            placeholder={t("login.phonePlaceholder")}
            value={phone}
            onChange={(event) => setPhone(toLatinDigits(event.target.value))}
            onBlur={() => setPhone(normalizePhoneForMarket(phone, tenant.countryCode))}
            aria-invalid={Boolean(error)}
            className="h-12 rounded-2xl text-center text-lg tracking-widest"
          />
          {error ? <p className="text-sm font-medium text-destructive">{error}</p> : null}
        </div>
        <Button type="submit" size="lg" disabled={loading}>
          {loading ? <Spinner /> : null}
          {t("login.send")}
        </Button>
      </form>
    );
  }

  if (step === "name") {
    return (
      <form className="grid gap-4" onSubmit={(event) => { event.preventDefault(); void handleComplete(); }}>
        <p className="text-sm text-muted-foreground">{t("login.nameStep")}</p>
        <div className="grid gap-2">
          <Label htmlFor="login-name">{t("orderType.fullName")}</Label>
          <Input
            id="login-name"
            autoFocus
            autoComplete="name"
            required
            minLength={2}
            maxLength={120}
            value={name}
            onChange={(event) => setName(event.target.value)}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? "login-name-error" : undefined}
            className="h-12"
          />
          {error ? <p id="login-name-error" className="text-sm font-medium text-destructive">{error}</p> : null}
        </div>
        <Button type="submit" size="lg" disabled={loading || name.trim().length < 2}>
          {loading ? <Spinner /> : null}
          {t("login.complete")}
        </Button>
        <Button
          type="button"
          variant="ghost"
          disabled={loading}
          onClick={() => { setStep("phone"); setCode(""); setError(null); }}
        >
          {t("login.changePhone")}
        </Button>
      </form>
    );
  }

  return (
    <div className="grid gap-4">
      <p className="text-sm text-muted-foreground">{t("login.otpStep", { phone })}</p>
      <div className="flex justify-center" dir="ltr">
        <InputOTP
          disabled={loading}
          maxLength={4}
          inputMode="numeric"
          autoComplete="one-time-code"
          aria-label={t("login.otpCode")}
          value={code}
          onChange={(v) => {
            const normalized = toLatinDigits(v);
            setCode(normalized);
            if (normalized.length === 4) handleVerify(normalized);
          }}
        >
          <InputOTPGroup>
            {[0, 1, 2, 3].map((i) => (
              <InputOTPSlot key={i} index={i} className="size-10 text-lg sm:size-12" />
            ))}
          </InputOTPGroup>
        </InputOTP>
      </div>
      {error ? <p className="text-center text-sm font-medium text-destructive">{error}</p> : null}
      <Button size="lg" onClick={() => handleVerify()} disabled={loading || code.length !== 4}>
        {loading ? <Spinner /> : null}
        {t("login.verify")}
      </Button>
      <Button variant="ghost" onClick={handleResend} disabled={resending || loading}>
        {resending ? <Spinner /> : null}
        {t("login.resend")}
      </Button>
      <Button variant="ghost" disabled={loading || resending} onClick={() => { setStep("phone"); setCode(""); setError(null); }}>
        {t("common.change")}
      </Button>
    </div>
  );
}
